import React, { useState, Fragment } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import styled from 'styled-components';
import {
    Card,
    PreviewContainer,
    PreviewImage,
    FallbackPreview,
    Content,
    Title,
    Url,
    LoadingOverlay
} from '../../styles/BookmarkCard.styles';
import { BookmarkIcon } from '../icons/BookmarkIcon';
import BookmarkMenu from './BookmarkMenu';
import EditBookmarkModal from './EditBookmarkModal';

const StyledCard = styled(Card)`
    &:hover {
        box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
        
        & > div:first-child {
            opacity: 1;
        }
    }
`;

const BookmarkCard = ({ bookmark, index, onRemove, onUpdate, isCompactView }) => {
    const { url, title, previewImage } = bookmark;
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    
    const handleClick = (e) => {
        // Prevent opening URL if clicking menu or modal
        if (e.target.closest('button') || e.target.closest('.modal')) return;
        
        // Open URL in new tab
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleRemove = (e) => {
        e.stopPropagation();
        onRemove(bookmark.id);
    };

    const formatUrl = (url) => {
        try {
            const urlObj = new URL(url);
            return urlObj.hostname;
        } catch {
            return url;
        }
    };

    return (
        <Fragment>
            <Draggable draggableId={bookmark.id} index={index}>
                {(provided, snapshot) => (
                    <StyledCard
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        onClick={handleClick}
                        style={{
                            ...provided.draggableProps.style,
                            opacity: snapshot.isDragging ? 0.8 : 1
                        }}
                        isCompactView={isCompactView}
                    >
                        {/* Pass bookmark.url as bookmarkUrl prop */}
                        <BookmarkMenu
                            onEdit={() => setIsEditModalOpen(true)}
                            onRemove={handleRemove}
                            bookmarkUrl={bookmark.url}
                        />
                        <PreviewContainer>
                            {previewImage ? (
                                <PreviewImage
                                    src={previewImage}
                                    alt={title}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextElementSibling.style.display = 'flex';
                                    }}
                                />
                            ) : null}
                            <FallbackPreview
                                style={{ display: previewImage ? 'none' : 'flex' }}
                            >
                                <BookmarkIcon />
                            </FallbackPreview>
                        </PreviewContainer>
                        <Content>
                            <Title>{title || 'Untitled Bookmark'}</Title>
                            <Url>{formatUrl(url)}</Url>
                        </Content>
                    </StyledCard>
                )}
            </Draggable>
            {isEditModalOpen ? (
                <EditBookmarkModal
                    bookmark={bookmark}
                    onClose={() => setIsEditModalOpen(false)}
                    onSave={(updatedBookmark) => {
                        onUpdate(updatedBookmark);
                        setIsEditModalOpen(false);
                    }}
                />
            ) : null}
        </Fragment>
    );
};

export default BookmarkCard;